import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import CopyToClipboard from "react-copy-to-clipboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClipboard } from "@fortawesome/free-solid-svg-icons";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { AuthenticateSelect } from "../../../../auth/select";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { Main, Styled } from "./style";

type WhatsAppCloudTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

type ListenMode = "all_messages" | "specific_groups_only" | "dm_only";

const randomVerifyToken = () =>
  `wa_cloud_${Math.random().toString(36).slice(2, 12)}_${Date.now()}`;

const normalizeListenMode = (value: unknown): ListenMode => {
  if (
    value === "all_messages" ||
    value === "specific_groups_only" ||
    value === "dm_only"
  ) {
    return value;
  }
  return "all_messages";
};

const normalizeTriggerIncoming = (value: unknown): boolean =>
  typeof value === "boolean" ? value : true;

const normalizeTriggerOutgoing = (value: unknown): boolean =>
  typeof value === "boolean" ? value : false;

const normalizeTriggerStatus = (value: unknown): boolean =>
  typeof value === "boolean" ? value : false;

export function WhatsAppCloudTrigger({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: WhatsAppCloudTriggerProps) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isCopyUrl, setIsCopyUrl] = React.useState(false);
  const [isCopyToken, setIsCopyToken] = React.useState(false);
  const [directionErrorMessage, setDirectionErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setIsExpanded((p) => !p));
    if (setChildSettings) setChildSettings(() => setSettings((p) => ({ ...p, isToggle: !p.isToggle })));
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value?.verifyToken) {
      updateNodeValue(id, setNodes, { verifyToken: randomVerifyToken() });
    }
  }, [data.value?.verifyToken, id, setNodes]);

  React.useEffect(() => {
    const nextValues = {
      listenMode: normalizeListenMode(data.value?.listenMode),
      triggerIncoming: normalizeTriggerIncoming(data.value?.triggerIncoming),
      triggerOutgoing: normalizeTriggerOutgoing(data.value?.triggerOutgoing),
      triggerStatus: normalizeTriggerStatus(data.value?.triggerStatus),
    };

    if (
      nextValues.listenMode !== data.value?.listenMode ||
      nextValues.triggerIncoming !== data.value?.triggerIncoming ||
      nextValues.triggerOutgoing !== data.value?.triggerOutgoing ||
      nextValues.triggerStatus !== data.value?.triggerStatus
    ) {
      updateNodeValue(id, setNodes, nextValues);
    }
  }, [
    data.value?.listenMode,
    data.value?.triggerIncoming,
    data.value?.triggerOutgoing,
    data.value?.triggerStatus,
    id,
    setNodes,
  ]);

  const listenMode = normalizeListenMode(data.value?.listenMode);
  const triggerIncoming = normalizeTriggerIncoming(data.value?.triggerIncoming);
  const triggerOutgoing = normalizeTriggerOutgoing(data.value?.triggerOutgoing);
  const triggerStatus = normalizeTriggerStatus(data.value?.triggerStatus);

  const persistTriggerConfig = React.useCallback(
    (nextValues: Partial<Record<"listenMode" | "triggerIncoming" | "triggerOutgoing" | "triggerStatus", any>>) => {
      updateNodeValue(id, setNodes, {
        listenMode,
        triggerIncoming,
        triggerOutgoing,
        triggerStatus,
        ...nextValues,
      });
    },
    [id, listenMode, setNodes, triggerIncoming, triggerOutgoing, triggerStatus],
  );

  const projectId = React.useMemo(
    () => new URLSearchParams(window.location.search).get("id") || "",
    [],
  );
  const connectionId = data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id || "";
  const verifyToken = data.value?.connection?.verifyToken || data.value?.verifyToken || "";
  const webhookUrl =
    projectId && connectionId
      ? `${webURL}/triggers/whatsapp-cloud/webhook?projectId=${projectId}&nodeId=${id}&connectionId=${connectionId}`
      : "Select a WhatsApp Cloud API connection to generate the callback URL.";
  const isWarning = !connectionId;
  const listenerModeNote =
    listenMode === "all_messages"
      ? ""
      : "WhatsApp Cloud API does not expose group chat listeners here yet, so only All messages is currently active.";

  const handleModeChange = (nextMode: ListenMode) => {
    setDirectionErrorMessage("");
    persistTriggerConfig({ listenMode: nextMode });
  };

  const handleDirectionChange = (
    direction: "incoming" | "outgoing",
    checked: boolean,
  ) => {
    const nextTriggerIncoming = direction === "incoming" ? checked : triggerIncoming;
    const nextTriggerOutgoing = direction === "outgoing" ? checked : triggerOutgoing;

    if (!nextTriggerIncoming && !nextTriggerOutgoing) {
      setDirectionErrorMessage("At least one direction must be enabled.");
      return;
    }

    setDirectionErrorMessage("");
    persistTriggerConfig({
      triggerIncoming: nextTriggerIncoming,
      triggerOutgoing: nextTriggerOutgoing,
    });
  };

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}
        {isExpanded && (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            style={{ minWidth: "460px" }}
            isWarning={isWarning}
          >
            <Styled>
              <AuthenticateSelect
                type="whatsappCloud"
                defaultId={connectionId}
                handleAuthenticate={(type, connection) => {
                  if (type === "add") {
                    updateNodeValue(id, setNodes, {
                      connection,
                      connectionId: connection._id || connection.id,
                      verifyToken: connection.verifyToken || data.value?.verifyToken,
                    });
                  } else {
                    updateNodeValue(id, setNodes, {
                      connection: null,
                      connectionId: null,
                    });
                  }
                }}
                withLine={true}
                background={data.background}
              />

              <div className="section">
                <div className="label">Listener Mode</div>
                <div className="modeRow">
                  <label>
                    <input
                      type="radio"
                      checked={listenMode === "all_messages"}
                      onChange={() => handleModeChange("all_messages")}
                    />
                    <span>All messages</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={listenMode === "specific_groups_only"}
                      onChange={() => handleModeChange("specific_groups_only")}
                    />
                    <span>Specific groups only</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={listenMode === "dm_only"}
                      onChange={() => handleModeChange("dm_only")}
                    />
                    <span>DM only (not implemented)</span>
                  </label>
                </div>
                {listenerModeNote ? (
                  <div className="helper-text">{listenerModeNote}</div>
                ) : null}
              </div>

              <div className="section">
                <div className="label">Message Direction</div>
                <div className="directionRow">
                  <label>
                    <input
                      type="checkbox"
                      checked={triggerIncoming}
                      onChange={(event) =>
                        handleDirectionChange("incoming", event.target.checked)
                      }
                    />
                    <span>Incoming messages</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={triggerOutgoing}
                      onChange={(event) =>
                        handleDirectionChange("outgoing", event.target.checked)
                      }
                    />
                    <span>Outgoing messages</span>
                  </label>
                </div>
                <div className="helper-text">
                  Default: Incoming messages enabled, outgoing messages disabled.
                </div>
                {directionErrorMessage ? (
                  <div className="sectionError">{directionErrorMessage}</div>
                ) : null}
              </div>

              <div className="section">
                <div className="label">Status Messages</div>
                <label className="toggleRow">
                  <input
                    type="checkbox"
                    checked={triggerStatus}
                    onChange={(event) => {
                      persistTriggerConfig({ triggerStatus: event.target.checked });
                    }}
                  />
                  <span>Trigger WhatsApp Status</span>
                </label>
              </div>

              <div className="webhook-section">
                <h4>Callback URL</h4>
                <CopyToClipboard
                  text={webhookUrl}
                  onCopy={() => {
                    setIsCopyUrl(true);
                    setTimeout(() => setIsCopyUrl(false), 800);
                  }}
                >
                  <div className="link-container" title={webhookUrl}>
                    <div className="link-with-copy">
                      <FontAwesomeIcon icon={isCopyUrl ? faCheck : faClipboard} className={`copy-icon ${isCopyUrl ? "copied" : ""}`} />
                      <span className="link-text">{webhookUrl}</span>
                    </div>
                  </div>
                </CopyToClipboard>
              </div>

              <div className="webhook-section">
                <h4>Verify Token</h4>
                <CopyToClipboard
                  text={verifyToken}
                  onCopy={() => {
                    setIsCopyToken(true);
                    setTimeout(() => setIsCopyToken(false), 800);
                  }}
                >
                  <div className="link-container" title={verifyToken}>
                    <div className="link-with-copy">
                      <FontAwesomeIcon icon={isCopyToken ? faCheck : faClipboard} className={`copy-icon ${isCopyToken ? "copied" : ""}`} />
                      <span className="link-text">{verifyToken}</span>
                    </div>
                  </div>
                </CopyToClipboard>
              </div>

              <p className="hint">
                Add the callback URL and verify token in Meta Developer webhooks, then subscribe to WhatsApp messages.
              </p>
            </Styled>
          </ExpandedBlock>
        )}
      </Main>
    </>
  );
}
